import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { setAuthTokenProvider } from '../utils/apiClient';

const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID ?? '';
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID ?? '';

const DEV_AUTO_LOGIN =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';

function hasConfig(): boolean {
  return !!USER_POOL_ID && !!USER_POOL_CLIENT_ID;
}

const userPool = hasConfig()
  ? new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: USER_POOL_CLIENT_ID,
    })
  : null;

const DEV_USER: AuthUser = { userId: 'dev-user', email: 'dev@local' };

export interface AuthUser {
  userId: string;
  email: string;
}

interface PendingPasswordChallenge {
  user: CognitoUser;
  userAttributes: Record<string, string>;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  configured: boolean;
  pendingChallenge: PendingPasswordChallenge | null;
  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function userFromSession(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload() as {
    sub: string;
    email?: string;
  };
  return { userId: payload.sub, email: payload.email ?? '' };
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(DEV_AUTO_LOGIN ? DEV_USER : null);
  const [loading, setLoading] = useState(!DEV_AUTO_LOGIN);
  const [currentSession, setCurrentSession] = useState<CognitoUserSession | null>(null);
  const [pendingChallenge, setPendingChallenge] =
    useState<PendingPasswordChallenge | null>(null);

  const sessionRef = useRef<CognitoUserSession | null>(null);
  sessionRef.current = currentSession;

  useEffect(() => {
    const getter = () => {
      const s = sessionRef.current;
      return s?.isValid() ? s.getIdToken().getJwtToken() : null;
    };
    setAuthTokenProvider(getter);
    if (typeof window !== 'undefined') {
      (window as unknown as { __authToken?: () => string | null }).__authToken = getter;
    }
  }, []);

  useEffect(() => {
    if (DEV_AUTO_LOGIN) return;
    if (!userPool) {
      setLoading(false);
      return;
    }
    const existing = userPool.getCurrentUser();
    if (!existing) {
      setLoading(false);
      return;
    }
    existing.getSession((err: Error | null, session: CognitoUserSession | null) => {
      setLoading(false);
      if (err || !session || !session.isValid()) return;
      setCurrentSession(session);
      setUser(userFromSession(session));
    });
  }, []);

  const signIn = useCallback(
    (email: string, password: string) =>
      new Promise<void>((resolve, reject) => {
        if (!userPool) {
          reject(new Error('Cognito is not configured. Set VITE_USER_POOL_ID + VITE_USER_POOL_CLIENT_ID.'));
          return;
        }
        const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
        const details = new AuthenticationDetails({ Username: email, Password: password });
        cognitoUser.authenticateUser(details, {
          onSuccess: (session) => {
            setCurrentSession(session);
            setUser(userFromSession(session));
            setPendingChallenge(null);
            resolve();
          },
          onFailure: (err) => reject(err),
          newPasswordRequired: (_userAttributes) => {
            // Cognito returns the user's existing attributes here. We must NOT
            // echo any of them back in completeNewPasswordChallenge because the
            // pool marks them immutable (email) — doing so triggers
            // "Cannot modify an already provided email". Send an empty map.
            setPendingChallenge({ user: cognitoUser, userAttributes: {} });
            resolve();
          },
        });
      }),
    []
  );

  const completeNewPassword = useCallback(
    (newPassword: string) =>
      new Promise<void>((resolve, reject) => {
        if (!pendingChallenge) {
          reject(new Error('No pending password challenge'));
          return;
        }
        pendingChallenge.user.completeNewPasswordChallenge(
          newPassword,
          pendingChallenge.userAttributes,
          {
            onSuccess: (session) => {
              setCurrentSession(session);
              setUser(userFromSession(session));
              setPendingChallenge(null);
              resolve();
            },
            onFailure: reject,
          }
        );
      }),
    [pendingChallenge]
  );

  const signOut = useCallback(() => {
    const existing = userPool?.getCurrentUser();
    existing?.signOut();
    setUser(null);
    setCurrentSession(null);
    // Clear the /me cache so the next user doesn't inherit prior state.
    void import('../hooks/useMe').then((m) => m.clearMeCache()).catch(() => {});
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: DEV_AUTO_LOGIN || !!userPool,
      pendingChallenge,
      signIn,
      completeNewPassword,
      signOut,
    }),
    [user, loading, pendingChallenge, signIn, completeNewPassword, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
